import { Repository } from "./adapters/repository";
import { HttpClient } from "./adapters/httpClient";
import { FileSystem } from "./adapters/fileSystem";

import * as retrieveDocuments from "./useCases/retrieveDocuments";
import * as uploadDocument from "./useCases/uploadDocument";
import * as createFolder from "./useCases/createFolder";
import * as removeFolder from "./useCases/removeFolder";
import * as removeDocument from "./useCases/removeDocument";
import * as downloadDocumentContent from "./useCases/downloadDocumentContent";
import * as downloadFolderContent from "./useCases/downloadFolderContent";
import * as attachDocumentsToElement from "./useCases/attachDocumentsToElement";
import * as removeAttachedDocument from "./useCases/removeAttachedDocument";
import * as retrieveElementAttachments from "./useCases/retrieveElementAttachments";
import * as moveDocument from "./useCases/moveDocument";
import * as retrieveDocumentTemplatesFolder from "./useCases/retrieveDocumentTemplatesFolder";
import * as createDocument from "./useCases/createDocument";
import * as retrieveDocumentKey from "./useCases/retrieveDocumentKey";
import * as retrieveDocumentEditionToken from "./useCases/retrieveDocumentEditionToken";
import * as updateFolder from "./useCases/updateFolder";
import * as copyDocument from "./useCases/copyDocument";
import * as updateDocument from "./useCases/updateDocument";
import * as retrievePrivateDocuments from "./useCases/retrievePrivateDocuments";
import * as moveFolder from "./useCases/moveFolder";

enum UseCases {
  retrieveDocuments = "retrieveDocuments",
  uploadDocument = "uploadDocument",
  createFolder = "createFolder",
  removeFolder = "removeFolder",
  removeDocument = "removeDocument",
  downloadDocumentContent = "downloadDocumentContent",
  downloadFolderContent = "downloadFolderContent",
  attachDocumentsToElement = "attachDocumentsToElement",
  removeAttachedDocument = "removeAttachedDocument",
  retrieveElementAttachments = "retrieveElementAttachments",
  moveDocument = "moveDocument",
  retrieveDocumentTemplatesFolder = "retrieveDocumentTemplatesFolder",
  createDocument = "createDocument",
  retrieveDocumentKey = "retrieveDocumentKey",
  retrieveDocumentEditionToken = "retrieveDocumentEditionToken",
  updateFolder = "updateFolder",
  copyDocument = "copyDocument",
  updateDocument = "updateDocument",
  retrievePrivateDocuments = "retrievePrivateDocuments",
  moveFolder = "moveFolder",
}

interface Core {
  [UseCases.retrieveDocuments]: retrieveDocuments.Interactor;
  [UseCases.uploadDocument]: uploadDocument.Interactor;
  [UseCases.createFolder]: createFolder.Interactor;
  [UseCases.removeFolder]: removeFolder.Interactor;
  [UseCases.removeDocument]: removeDocument.Interactor;
  [UseCases.downloadDocumentContent]: downloadDocumentContent.Interactor;
  [UseCases.downloadFolderContent]: downloadFolderContent.Interactor;
  [UseCases.attachDocumentsToElement]: attachDocumentsToElement.Interactor;
  [UseCases.removeAttachedDocument]: removeAttachedDocument.Interactor;
  [UseCases.retrieveElementAttachments]: retrieveElementAttachments.Interactor;
  [UseCases.moveDocument]: moveDocument.Interactor;
  [UseCases.retrieveDocumentTemplatesFolder]: retrieveDocumentTemplatesFolder.Interactor;
  [UseCases.createDocument]: createDocument.Interactor;
  [UseCases.retrieveDocumentKey]: retrieveDocumentKey.Interactor;
  [UseCases.retrieveDocumentEditionToken]: retrieveDocumentEditionToken.Interactor;
  [UseCases.updateFolder]: updateFolder.Interactor;
  [UseCases.copyDocument]: copyDocument.Interactor;
  [UseCases.updateDocument]: updateDocument.Interactor;
  [UseCases.retrievePrivateDocuments]: retrievePrivateDocuments.Interactor;
  [UseCases.moveFolder]: moveFolder.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = {
  repository: Repository;
  httpClient: HttpClient;
  fileSystem: FileSystem;
};

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.retrieveDocuments]: retrieveDocuments.createInteractor(
      dependencies.repository,
    ),
    [UseCases.uploadDocument]: uploadDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createFolder]: createFolder.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeFolder]: removeFolder.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeDocument]: removeDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.downloadDocumentContent]:
      downloadDocumentContent.createInteractor(
        dependencies.httpClient,
        dependencies.fileSystem,
      ),
    [UseCases.downloadFolderContent]: downloadFolderContent.createInteractor(
      dependencies.httpClient,
      dependencies.fileSystem,
    ),
    [UseCases.attachDocumentsToElement]:
      attachDocumentsToElement.createInteractor(dependencies.repository),
    [UseCases.removeAttachedDocument]: removeAttachedDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveElementAttachments]:
      retrieveElementAttachments.createInteractor(dependencies.repository),
    [UseCases.moveDocument]: moveDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveDocumentTemplatesFolder]:
      retrieveDocumentTemplatesFolder.createInteractor(dependencies.repository),
    [UseCases.createDocument]: createDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveDocumentKey]: retrieveDocumentKey.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveDocumentEditionToken]:
      retrieveDocumentEditionToken.createInteractor(dependencies.repository),
    [UseCases.updateFolder]: updateFolder.createInteractor(
      dependencies.repository,
    ),
    [UseCases.copyDocument]: copyDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.updateDocument]: updateDocument.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrievePrivateDocuments]:
      retrievePrivateDocuments.createInteractor(dependencies.repository),
    [UseCases.moveFolder]: moveFolder.createInteractor(dependencies.repository),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
